import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Popover,
  Typography,
} from '@mui/material';
import { ReactElement, ReactNode, cloneElement, useState } from 'react';
import { useLocales } from 'src/locales';

type CPConfirmProps = {
  title?: ReactNode;
  description?: ReactNode;
  onConfirm: () => void;
  id?: string;
  children: ReactElement;
};

export const CPConfirm = ({
  title,
  description,
  id: idProp = 'confirm-action',
  children,
  onConfirm,
}: CPConfirmProps) => {
  const { translate } = useLocales();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? idProp : undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {cloneElement(children, {
        onClick: handleClick,
      })}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Card data-testid="confirm-modal">
          <CardHeader
            title={title ?? translate('shared.confirmation.are_you_sure_title')}
            titleTypographyProps={{ variant: 'subtitle1' }}
            sx={{ px: 2, pt: 1.5 }}
          />

          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2">
              {description ??
                translate('shared.confirmation.this_action_cannot_be_undone')}
            </Typography>
          </CardContent>

          <CardActions sx={{ px: 2, pb: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={handleClose} size="small" color="inherit">
              {translate('shared.buttons.cancel')}
            </Button>
            <Button
              onClick={() => {
                handleClose();
                onConfirm();
              }}
              size="small"
              variant="contained"
              color="primary"
            >
              {translate('shared.buttons.confirm')}
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </>
  );
};
